// src/CreateMaestranzaForm.js
import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Button, TextField, Stack, Typography, Paper, List, ListItem, ListItemButton
} from '@mui/material';
import { API_BASE, authHeaders } from './api';
import RoleSelect from './components/RoleSelect';
import useDirtyGuard from './hooks/useDirtyGuard';
import ProfileCoachPanel from './components/ProfileCoachPanel';
import { fetchJson } from './api';
import ProgramAutocomplete from './ProgramAutocomplete';
import { useTranslation } from 'react-i18next';

// YYYY-MM-DD -> YYYY-MM-DD (+N giorni), gestisce fine mese/anno
const addDaysYmd = (ymd, days) => {
  if (!ymd) return '';
  const s = String(ymd).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const dt = new Date(Date.UTC(+m[1], +m[2]-1, +m[3]));
  dt.setUTCDate(dt.getUTCDate() + (days || 0));
  const y = dt.getUTCFullYear();
  const mo = String(dt.getUTCMonth()+1).padStart(2,'0');
  const d  = String(dt.getUTCDate()).padStart(2,'0');
  return `${y}-${mo}-${d}`;
};

// Se il locale è avanti a UTC (Roma), pre-compensa di +1 giorno
const bumpForLocalMidnightBug = (ymd) => {
  const tz = new Date().getTimezoneOffset(); // Roma: -60 o -120
  return (tz <= -60) ? addDaysYmd(ymd, 1) : ymd;
};

// "YYYY-MM-DD" -> "YYYY-MM-DDT12:00:00.000Z" (dopo bump)
const toUtcNoonIsoSafe = (ymd) => {
  const d = bumpForLocalMidnightBug(ymd);
  if (!d) return '';
  return `${d}T12:00:00.000Z`;
};


// Converte "YYYY-MM-DD" in "YYYY-MM-DDT12:00:00.000Z" (mezzogiorno UTC)
const toUtcNoonIso = (ymd) => {
  if (!ymd) return '';
  const d = String(ymd).slice(0, 10);
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return `${m[1]}-${m[2]}-${m[3]}T12:00:00.000Z`;
};

const toIsoDate = (s) => {
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // rimuove gli slash inutilmente escapati: [/-] (niente warning ESLint)
  const m = s.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const dt = new Date(s);
  return isNaN(dt) ? s : dt.toISOString().slice(0, 10);
};

export default function CreateMaestranzaForm({ onSaved, onCreated, onMaestranzaCreated, onCancel }) {
  const { t } = useTranslation();
  const [azienda, setAzienda] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const aziendaRef = React.useRef(null);
  const [produzione, setProduzione] = useState('');
  // Selezione dal catalogo programmi (null = testo libero / nessuna selezione)
  // { program: {id, name, is_verified}|null, edition: {id, edition_label, year}|null, freeText: string }
  const [programSelection, setProgramSelection] = useState(null);
  const [ruoli, setRuoli] = useState([]);     // array per RoleSelect
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isTentative, setIsTentative] = useState(false);
  const submittingRef = useRef(false);

  // ruoli master caricati da backend
  const [rolesMaster, setRolesMaster] = useState({});
  const [loadingRoles, setLoadingRoles] = useState(true);
  const { markDirty, resetDirty, confirmIfDirty } = useDirtyGuard();

  const searchCompanies = React.useCallback(async (q) => {
    if (!q || q.length < 2) { setCompanySuggestions([]); return; }
    try {
      setSearchingCompanies(true);
      const data = await fetchJson(`/maestranze/companies/search?q=${encodeURIComponent(q)}`);
      setCompanySuggestions(Array.isArray(data) ? data : []);
      setShowSuggestions(true);
    } catch { setCompanySuggestions([]); }
    finally { setSearchingCompanies(false); }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingRoles(true);
        const res = await fetch(`${API_BASE}/ruoli`, { headers: { ...authHeaders() } });
        const text = await res.text();
        let payload = {};
        try { payload = text ? JSON.parse(text) : {}; } catch { payload = {}; }
        if (!res.ok) throw new Error(payload?.error || `HTTP ${res.status}`);
        if (alive) setRolesMaster(payload || {});
      } catch (e) {
        console.error('Caricamento ruoli fallito:', e?.message || e);
        if (alive) setRolesMaster({});
      } finally {
        if (alive) setLoadingRoles(false);
      }
    })();
    return () => { alive = false; };
  }, []);
  const handleSubmit = async (e) => {
  e.preventDefault();

  // ✅ blocco immediato (sincrono), evita doppio submit
  if (submittingRef.current) return;
  submittingRef.current = true;

  if (saving) { submittingRef.current = false; return; } // extra safety
  setError('');

    if (!azienda || !produzione || !dataFine || !ruoli || ruoli.length === 0) {
      setError('Compila tutti i campi obbligatori.');
      submittingRef.current = false;
      return;
    }

    // fallback richiesto dal backend: mandiamo comunque data_inizio_contratto
    const dataInizioDaInviare = toIsoDate(dataFine);

    setSaving(true);
    try {
      // Compone il campo produzione: se l'utente ha selezionato dal catalogo,
      // usa "[program_name] [edition_label]"; altrimenti usa il testo libero del campo.
      let produzioneFinale = produzione;
      if (programSelection?.program && !programSelection.freeText) {
        const edLabel = programSelection.edition?.edition_label ?? '';
        produzioneFinale = edLabel
          ? `${programSelection.program.name} ${edLabel}`
          : programSelection.program.name;
      }

      const body = {
        azienda,
        produzione: produzioneFinale,
        ruolo: ruoli[0],
        ruoli,
        data_inizio_contratto: toUtcNoonIso(dataInizio || dataFine),
        data_fine_contratto: toUtcNoonIso(dataFine),
        is_tentative: isTentative,
      };

      if (!String(body.produzione || "").trim()) {
        setError("Inserisci il nome della produzione");
        submittingRef.current = false;
        return;
      }

        const res = await fetch(`${API_BASE}/maestranze`, {
          method: 'POST',
          headers: {
            ...authHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

      const text = await res.text();
      let payload = {};
      try { payload = text ? JSON.parse(text) : {}; } catch { payload = {}; }

      if (!res.ok) {
        throw new Error(payload?.error || `Errore salvataggio: ${res.status}`);
      }

      // 🔔 Notifica entrambe le possibili callback (back-compat + nuova dashboard)
      const cb = onSaved || onCreated || onMaestranzaCreated;
      if (typeof cb === 'function') cb(payload);

      // Se l'utente ha selezionato programma + edizione dal catalogo,
      // crea il credito CV collegato (silenzioso: 409 = già esiste, altri errori = solo log).
      const maestranzaId = payload?.id ?? payload?.maestranza?.id ?? null;
      const editionId = programSelection?.edition?.id ?? null;
      if (editionId && maestranzaId && ruoli.length > 0) {
        const creditBody = {
          edition_id: editionId,
          role: ruoli[0],
          source_maestranza_id: maestranzaId,
        };
        if (dataInizio) creditBody.self_start = toUtcNoonIso(dataInizio).slice(0, 10);
        if (dataFine)   creditBody.self_end   = toUtcNoonIso(dataFine).slice(0, 10);
        fetchJson('/cv/credits', {
          method: 'POST',
          body: JSON.stringify(creditBody),
        }).catch(e => {
          // 409 Conflict = credito già esistente → silenzioso
          const msg = e?.message || '';
          if (!msg.includes('409') && !msg.toLowerCase().includes('già esistente')) {
            console.warn('[CreateMaestranzaForm] POST /cv/credits fallito (non-409):', e);
          }
        });
      }

      resetDirty();
      if (typeof onCancel === 'function') onCancel();

      // (opzionale) reset locali
      setAzienda(''); setProduzione(''); setRuoli([]); setDataFine(''); setProgramSelection(null);
    } catch (err) {
      setError(err.message || 'Errore durante il salvataggio.');
    } finally {
      setSaving(false);
      submittingRef.current = false;
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} onChangeCapture={markDirty} sx={{ width: "100%", minWidth: { xs: "100%", sm: 340 }, display: "grid", gap: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Aggiungi Produzione</Typography>

      <Box sx={{ position: 'relative' }} ref={aziendaRef}>
        <TextField
          label="Società"
          value={azienda}
          onChange={(e) => {
            setAzienda(e.target.value);
            searchCompanies(e.target.value);
          }}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          onFocus={() => { if (companySuggestions.length > 0) setShowSuggestions(true); }}
          required
          fullWidth
          autoComplete="off"
        />
        {showSuggestions && companySuggestions.length > 0 && (
          <Paper elevation={8} sx={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
            bgcolor: '#1a1d23', border: '1px solid rgba(212,165,80,0.3)', borderRadius: 1, mt: 0.5
          }}>
            <List dense disablePadding>
              {companySuggestions.map(c => (
                <ListItemButton key={c.id}
                  onMouseDown={() => {
                    setAzienda(c.name);
                    setShowSuggestions(false);
                    setCompanySuggestions([]);
                  }}
                  sx={{ py: 0.8, '&:hover': { bgcolor: 'rgba(212,165,80,0.08)' } }}
                >
                  <Typography sx={{ fontSize: 13, color: '#f0ece4' }}>{c.name}</Typography>
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      {/* Campo produzione: autocomplete catalogo (solo inserimento nuovo) */}
      <Box>
        <Typography
          variant="caption"
          sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}
        >
          {t('crediti.label_cerca_programma')}
        </Typography>
        <ProgramAutocomplete
          value={produzione}
          onChange={(sel) => {
            setProgramSelection(sel);
            if (sel.freeText) {
              // testo libero: aggiorna direttamente il campo produzione
              setProduzione(sel.freeText);
            } else if (sel.program) {
              // dal catalogo: il nome verrà composto in handleSubmit
              // Qui impostiamo un valore visivo temporaneo per superare la validazione
              const edLabel = sel.edition?.edition_label ?? '';
              setProduzione(edLabel
                ? `${sel.program.name} ${edLabel}`
                : sel.program.name
              );
            } else {
              // reset (nessuna selezione)
              setProduzione('');
            }
          }}
          disabled={saving}
        />
      </Box>

      <RoleSelect
        rolesMaster={rolesMaster}
        label={loadingRoles ? 'Carico ruoli…' : 'Ruolo'}
        value={ruoli}
        onChange={(vals) => setRuoli(Array.isArray(vals) ? vals : (vals ? [vals] : []))}
        required
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
  <TextField
    label="Data inizio contratto"
    type="date"
    value={dataInizio}
    onChange={(e) => setDataInizio(e.target.value)}
    InputLabelProps={{ shrink: true }}
    fullWidth
    sx={{ flex: 1, minWidth: 0 }}
  />
  <TextField
    label="Data fine"
    type="date"
    value={dataFine}
    onChange={(e) => setDataFine(e.target.value)}
    InputLabelProps={{ shrink: true }}
    fullWidth
    required
    sx={{ flex: 1, minWidth: 0 }}
  />
</Stack>



      {error && <Typography color="error">{error}</Typography>}

      <Box sx={{ display:'flex', alignItems:'center', gap:1, mt:1, mb:1 }}>
        <input type="checkbox" id="isTentative" checked={isTentative} onChange={e => setIsTentative(e.target.checked)}
          style={{ width:16, height:16, accentColor:'#a78bfa', cursor:'pointer' }} />
        <label htmlFor="isTentative" style={{ fontSize:13, color: isTentative ? '#a78bfa' : '#8a8a9a', cursor:'pointer', userSelect:'none' }}>
          Non confermata
        </label>
      </Box>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
  <Button
    variant="outlined"
    onClick={async () => {
      const ok = await confirmIfDirty();
      if (!ok) return;
      // chiudi la modale se hai un onCancel
      if (typeof onCancel === 'function') onCancel();
    }}
  >
    Annulla
  </Button>
  <Button type="submit" variant="contained" disabled={saving}>
    {saving ? 'Salvo…' : 'Salva'}
  </Button>
</Stack>

    </Box>
  );
}
